// BlogProvider.tsx
import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { BlogContext, IBlogData, defaultBlogData } from '../contexts/AppContext';
import { BlogService } from '../pages/Blog/services/BlogService';

interface BlogProviderProps {
  children: React.ReactNode;
}

const CACHE_TTL = 3600000; // 1 hour

export const BlogProvider: React.FC<BlogProviderProps> = ({ children }) => {
  const [blogData, setBlogData] = useState<IBlogData>(defaultBlogData);
  const { i18n } = useTranslation();

  const refreshBlogData = useCallback(async (language?: string) => {
    // Use provided language or current i18n language, default to French
    const currentLang = language || i18n.language || 'fr';
    
    setBlogData(prev => ({ ...prev, isLoading: true }));

    try {
      const blogService = new BlogService();
      
      // Pass language to getAllCategoriesNav for backend sorting
      const [categoriesResponse, partnersResponse] = await Promise.all([
        blogService.getAllCategoriesNav(currentLang),
        blogService.getAllPartnersNav()
      ]);

      // Process categories - simplified since we're using the full objects
      const categoriesList = categoriesResponse.data || [];

      // Process partners - simplified since we're using the full objects
      const partnersList = partnersResponse.data || [];

      //console.log('Processed categories:', categoriesList.length); // Debug
      //console.log('Processed partners:', partnersList.length); // Debug

      const newBlogData: IBlogData = {
        categories: categoriesList,
        partners: partnersList,
        isLoading: false,
        lastUpdated: Date.now(),
        currentLanguage: currentLang,
      };

      setBlogData(newBlogData);

      // Cache to localStorage with language-specific keys
      localStorage.setItem(`blogCategories_${currentLang}`, JSON.stringify(categoriesList));
      localStorage.setItem('blogPartners', JSON.stringify(partnersList));
      localStorage.setItem('blogDataTimestamp', Date.now().toString());
      localStorage.setItem('blogCurrentLanguage', currentLang);

    } catch (error) {
      console.error('Error refreshing blog data:', error);
      setBlogData(prev => ({ ...prev, isLoading: false }));
    }
  }, [i18n.language]);

   // Function to change language and refresh data
  const setLanguage = useCallback(async (language: string) => {
    await refreshBlogData(language);
  }, [refreshBlogData]);

  // Load cached data on mount and when language changes
  useEffect(() => {
    const loadCachedData = (language: string) => {
      try {
        const cachedCategories = localStorage.getItem(`blogCategories_${language}`);
        const cachedPartners = localStorage.getItem('blogPartners');
        const timestamp = localStorage.getItem('blogDataTimestamp');
        const cachedLanguage = localStorage.getItem('blogCurrentLanguage');

        if (cachedCategories && cachedPartners && timestamp && cachedLanguage === language) {
          const lastUpdated = parseInt(timestamp);
          if (Date.now() - lastUpdated < CACHE_TTL) {
            setBlogData({
              categories: JSON.parse(cachedCategories),
              partners: JSON.parse(cachedPartners),
              isLoading: false,
              lastUpdated,
              currentLanguage: language,
            });
            return true;
          }
        }
      } catch (error) {
        console.error('Error loading cached blog data:', error);
      }
      return false;
    };

    // Try to load from cache, if not available or expired, fetch fresh data
    const currentLang = i18n.language || 'fr';
    if (!loadCachedData(currentLang)) {
      refreshBlogData(currentLang);
    }
  }, [i18n.language, refreshBlogData]);

  // Refresh data when language changes
  useEffect(() => {
    // Only refresh if the language has actually changed
    if (i18n.language && i18n.language !== blogData.currentLanguage) {
      refreshBlogData(i18n.language);
    }
  }, [i18n.language, blogData.currentLanguage, refreshBlogData]);

  const contextValue = {
    blogData,
    setBlogData,
    refreshBlogData,
    setLanguage,
  };

  return (
    <BlogContext.Provider value={contextValue}>
      {children}
    </BlogContext.Provider>
  );
};